<script setup lang="ts">
import PdsButton from '@/components/pds/PdsButton.vue';
import PdsIcon from '@/components/pds/PdsIcon.vue';
import { onMounted, onUnmounted, ref } from 'vue';
import { deviceIcon, deviceTypeLabel } from '../devices/labels';
import type { PdsDevice } from '../devices/types';
import {
  isAgentHost,
  isPrinterDevice,
  isProbeable,
  portOf,
  useDevices,
} from '../devices/useDevices';
import AgentConnectModal from './AgentConnectModal.vue';
import LabeledPanel from './LabeledPanel.vue';

const FEEDBACK_MS = 1_500;

const {
  devices,
  status,
  link,
  notice,
  testing,
  load,
  probe,
  testPrint,
  isStation,
  agentUrlFor,
  useForStation,
} = useDevices();

const tested = ref<string | null>(null);
// L'écran de préparation à connecter : l'opérateur vérifie ou complète l'adresse de l'agent avant de continuer.
const configuring = ref<PdsDevice | null>(null);
const connect = (device: PdsDevice) => {
  if (isAgentHost(device)) configuring.value = device;
  else void probe(device);
};
const timers = new Set<ReturnType<typeof setTimeout>>();

const DECLARED = { planned: 'Prévu', installed: 'Installé', retired: 'Retiré' };

const linkOf = (device: PdsDevice) => link.value[device.id] ?? 'unknown';

const stateOf = (device: PdsDevice) => {
  if (!isProbeable(device)) return DECLARED[device.status];
  const state = linkOf(device);
  if (state === 'online') return 'Connectée';
  if (state === 'offline') return 'Hors ligne';
  return state === 'checking' ? 'Connexion…' : 'Non connectée';
};
const tone = (device: PdsDevice) => {
  if (!isProbeable(device))
    return device.status === 'installed'
      ? 'bg-succes-doux text-succes-texte'
      : 'bg-creux text-encre-douce';
  const state = linkOf(device);
  if (state === 'online') return 'bg-succes-doux text-succes-texte';
  if (state === 'offline') return 'bg-alerte-doux text-alerte-texte';
  return 'bg-creux text-encre-douce';
};
const addressOf = (device: PdsDevice) => {
  if (isAgentHost(device)) return `agent ${agentUrlFor(device)}`;
  return device.ip
    ? `${device.ip}${device.type === 'printer' || device.port ? `:${portOf(device)}` : ''}`
    : '';
};

const test = async (device: PdsDevice) => {
  if (!(await testPrint(device))) return;
  tested.value = device.id;
  const timer = setTimeout(() => {
    timers.delete(timer);
    tested.value = null;
  }, FEEDBACK_MS);
  timers.add(timer);
};

onMounted(load);
onUnmounted(() => timers.forEach(clearTimeout));
</script>

<template>
  <LabeledPanel label="Périphériques du réseau local">
    <div class="no-scrollbar flex min-h-0 flex-col overflow-y-auto">
      <p
        v-if="notice"
        role="alert"
        class="text-alerte-texte pb-4 text-[12px] leading-[17px]"
      >
        {{ notice }}
      </p>

      <p
        v-if="status === 'loading' && !devices.length"
        class="text-encre-douce text-[14px] leading-[1.4]"
      >
        Chargement des périphériques…
      </p>
      <div
        v-else-if="status === 'error'"
        role="alert"
        class="flex flex-col items-start gap-3"
      >
        <p class="text-alerte-texte text-[14px] leading-[1.4]">
          Impossible de charger les périphériques du commerce.
        </p>
        <div class="min-w-[120px]">
          <PdsButton variant="second" @click="load">Réessayer</PdsButton>
        </div>
      </div>
      <p
        v-else-if="status === 'ready' && !devices.length"
        class="text-encre-douce text-[14px] leading-[1.4]"
      >
        Aucun périphérique n’est déclaré pour ce commerce.
      </p>

      <ul v-else class="flex flex-col">
        <li
          v-for="device in devices"
          :key="device.id"
          class="border-filet flex flex-wrap items-center gap-[14px] border-t py-4 first:border-t-0 first:pt-0"
        >
          <span class="text-encre flex size-6 shrink-0 items-center">
            <PdsIcon
              :name="deviceIcon(device.type, linkOf(device) !== 'offline')"
            />
          </span>
          <div class="min-w-0 flex-1">
            <p
              class="flex flex-wrap items-center gap-2 text-[14px] leading-[20px] font-semibold"
            >
              {{ device.label }}
              <span
                v-if="isStation(device)"
                class="bg-creux text-encre-douce rounded-[8px] px-2 text-[12px] leading-[20px] font-semibold"
                data-testid="station-printer"
              >
                Ce poste
              </span>
            </p>
            <p class="text-encre-douce truncate text-[12px] leading-[17px]">
              {{
                [
                  deviceTypeLabel(device.type),
                  addressOf(device),
                  device.location,
                ]
                  .filter(Boolean)
                  .join(' · ')
              }}
            </p>
          </div>
          <span
            class="inline-flex h-7 items-center rounded-[8px] px-[10px] text-[12px] leading-[17px] font-semibold"
            :class="tone(device)"
          >
            {{ stateOf(device) }}
          </span>
          <div v-if="isProbeable(device)" class="flex gap-2">
            <template v-if="linkOf(device) === 'online'">
              <div class="min-w-[82px]">
                <PdsButton
                  variant="second"
                  :loading="testing === device.id"
                  @click="test(device)"
                >
                  {{ tested === device.id ? 'Test envoyé' : 'Tester' }}
                </PdsButton>
              </div>
              <div
                v-if="isPrinterDevice(device) && !isStation(device)"
                class="min-w-[96px]"
              >
                <PdsButton variant="second" @click="useForStation(device)">
                  Utiliser
                </PdsButton>
              </div>
            </template>
            <div v-else class="min-w-[110px]">
              <PdsButton
                variant="second"
                :loading="linkOf(device) === 'checking'"
                @click="connect(device)"
              >
                {{ linkOf(device) === 'offline' ? 'Reconnecter' : 'Connecter' }}
              </PdsButton>
            </div>
          </div>
        </li>
      </ul>
    </div>
  </LabeledPanel>

  <AgentConnectModal
    v-if="configuring"
    :device="configuring"
    @close="configuring = null"
    @connected="configuring = null"
  />
</template>
